// src/features/customerModule/customers/customerWorkbook.js
//
// Builds the all-tabs .xlsx: a Summary sheet plus one sheet per section from
// customerExportConfig.js. Sections are fetched sequentially so the button can
// report progress; a section that fails still gets a sheet, and the error is
// recorded on Summary.
//
// Requires: npm i exceljs
// (Webpack 5 / CRA: if you hit stream/buffer polyfill errors, switch to
//  `import ExcelJS from "exceljs/dist/exceljs.min.js";`)

import ExcelJS from "exceljs";
import { CUSTOMER_EXPORT_SECTIONS } from "./customerExportConfig.js";
import {
    toExcelValue,
    excelNumFmt,
    downloadBlob,
    dateStamp,
    safeFileSegment,
} from "./csvExport.js";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const HEADER_FILL = "FFEFF4FA";
const HEADER_FONT = "FF1F3B57";
const MAX_COL_WIDTH = 55;
const MIN_COL_WIDTH = 10;

// Excel reserves these; ExcelJS throws `The name "X" is protected` on them.
// "History" is the shared-workbook change log.
const RESERVED_SHEET_NAMES = new Set(["history"]);

// Excel also forbids : \ / ? * [ ] and caps names at 31 chars. Dupes must be unique.
const sanitizeSheetName = (name, used) => {
    let base = String(name || "Sheet").replace(/[:\\/?*[\]]/g, " ").trim().slice(0, 31) || "Sheet";
    if (RESERVED_SHEET_NAMES.has(base.toLowerCase())) base = `${base} Log`.slice(0, 31);

    let candidate = base;
    let n = 2;
    while (used.has(candidate.toLowerCase())) {
        const suffix = ` (${n++})`;
        candidate = base.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(candidate.toLowerCase());
    return candidate;
};

const styleHeaderRow = (row) => {
    row.font = { bold: true, color: { argb: HEADER_FONT } };
    row.alignment = { vertical: "middle" };
    row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
        cell.border = { bottom: { style: "thin", color: { argb: "FFD3DDE8" } } };
    });
    row.height = 18;
};

const autoWidth = (sheet, columns, rows) => {
    sheet.columns.forEach((column, i) => {
        const col = columns[i];
        if (!col) return;
        let widest = String(col.label ?? col.key ?? "").length;
        rows.forEach((row) => {
            const v = toExcelValue(row, col);
            const len = v instanceof Date ? 20 : String(v ?? "").length;
            if (len > widest) widest = len;
        });
        column.width = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, widest + 2));
    });
};

const addSectionSheet = (workbook, usedNames, section, rows) => {
    const sheet = workbook.addWorksheet(sanitizeSheetName(section.sheet, usedNames), {
        views: [{ state: "frozen", ySplit: 1 }],
    });

    const columns = section.columns || [];
    sheet.columns = columns.map((col) => ({ key: col.key || col.label }));

    styleHeaderRow(sheet.addRow(columns.map((col) => col.label ?? col.key)));

    rows.forEach((row) => {
        const added = sheet.addRow(columns.map((col) => toExcelValue(row, col)));
        columns.forEach((col, i) => {
            const fmt = excelNumFmt(col);
            if (fmt) added.getCell(i + 1).numFmt = fmt;
        });
        // Message / note bodies keep their line breaks readable.
        added.alignment = { vertical: "top", wrapText: true };
    });

    if (!rows.length) {
        sheet.addRow(["No records"]).getCell(1).font = { italic: true, color: { argb: "FF8A94A0" } };
    } else if (columns.length) {
        sheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: 1, column: columns.length },
        };
    }

    autoWidth(sheet, columns, rows);
    return sheet;
};

const addSummarySheet = (workbook, { profile, customerguid, results }) => {
    const sheet = workbook.addWorksheet("Summary");
    sheet.columns = [{ width: 22 }, { width: 34 }, { width: 46 }];

    const title = sheet.addRow(["Customer Export"]);
    title.font = { bold: true, size: 14 };
    sheet.addRow([]);

    const meta = [
        ["Customer", `${profile?.firstname ?? ""} ${profile?.lastname ?? ""}`.trim() || "—"],
        ["Customer ID", profile?.customerid ?? "—"],
        ["Customer GUID", customerguid ?? "—"],
        ["Generated", new Date()],
    ];
    meta.forEach(([label, value]) => {
        const row = sheet.addRow([label, value]);
        row.getCell(1).font = { bold: true };
        if (value instanceof Date) row.getCell(2).numFmt = "mm/dd/yyyy hh:mm AM/PM";
    });

    sheet.addRow([]);
    styleHeaderRow(sheet.addRow(["Sheet", "Rows", "Status"]));
    results.forEach(({ section, rows, error }) => {
        sheet.addRow([section.sheet, rows.length, error ? `Failed: ${error}` : "OK"]);
    });

    // Move Summary to the front regardless of creation order.
    const idx = workbook.worksheets.indexOf(sheet);
    workbook.worksheets.splice(idx, 1);
    workbook.worksheets.unshift(sheet);
    workbook.worksheets.forEach((ws, i) => { ws.orderNo = i; });

    return sheet;
};

/**
 * Fetch every section and build the workbook.
 * onProgress({ done, total, label }) fires before each section is fetched.
 */
export async function buildCustomerWorkbook({ customerguid, profile, onProgress }) {
    const sections = CUSTOMER_EXPORT_SECTIONS;
    const results = [];

    for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        onProgress?.({ done: i, total: sections.length, label: section.sheet });
        try {
            const rows = (await section.fetch?.(customerguid)) || [];
            results.push({ section, rows: Array.isArray(rows) ? rows : [rows] });
        } catch (e) {
            console.error(`Export: ${section.sheet} failed`, e);
            results.push({ section, rows: [], error: e?.message || "request failed" });
        }
    }
    onProgress?.({ done: sections.length, total: sections.length, label: "Building workbook" });

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "CaraFin";
    workbook.created = new Date();

    const usedNames = new Set();
    results.forEach(({ section, rows }) => addSectionSheet(workbook, usedNames, section, rows));
    addSummarySheet(workbook, { profile, customerguid, results });

    return { workbook, results };
}

export function customerWorkbookFilename(profile) {
    const who = safeFileSegment(profile?.customerid || profile?.lastname || "customer");
    return `customer_${who}_all_tabs_${dateStamp()}.xlsx`;
}

export async function exportCustomerWorkbook({ customerguid, profile, onProgress }) {
    const { workbook, results } = await buildCustomerWorkbook({ customerguid, profile, onProgress });
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob(customerWorkbookFilename(profile), new Blob([buffer], { type: XLSX_MIME }));
    return results;
}